import type { FeatureCollection, Point, Position } from 'geojson';
import type mapboxgl from 'mapbox-gl';
import { useCallback, useEffect, useMemo, useState } from 'react';

type OnBoundsChanged = (bounds: mapboxgl.LngLatBounds) => void;

interface GeometryWithCoords {
  geometry: { coordinates: Position };
}
type DataWithGeometry<T extends GeometryWithCoords> = GeometryWithCoords & T;
interface DataInBounds<T extends GeometryWithCoords> {
  dataInBounds?: DataWithGeometry<T>[];
  updateBounds: (bounds: mapboxgl.LngLatBounds) => void;
}

interface PointsInBounds<T> {
  pointsInBounds?: FeatureCollection<Point, T>;
  updateBounds: (bounds: mapboxgl.LngLatBounds) => void;
}
interface MapBounds {
  ne: mapboxgl.LngLat;
  sw: mapboxgl.LngLat;
}

export const useMapboxBoundsChange = (map?: mapboxgl.Map, onBoundsChanged?: OnBoundsChanged): void => {
  const handleBoundsChanged = useCallback(() => {
    if (!map) return;
    const currentZoom = map.getBounds();
    onBoundsChanged?.(currentZoom);
  }, [map, onBoundsChanged]);

  useEffect(() => {
    if (!map) return () => undefined;

    map.on('zoom', handleBoundsChanged);
    map.on('move', handleBoundsChanged);

    return () => {
      map.off('zoom', handleBoundsChanged);
      map.off('move', handleBoundsChanged);
    };
  }, [map, handleBoundsChanged]);
};

export const useDataInBounds = <T extends GeometryWithCoords>(data?: DataWithGeometry<T>[]): DataInBounds<T> => {
  const [currentBounds, setCurrentBounds] = useState<MapBounds>();
  const dataInBounds = useMemo<DataWithGeometry<T>[] | undefined>(() => {
    if (!currentBounds || !data) return undefined;
    const { ne, sw } = currentBounds;
    return data.filter(
      point =>
        point.geometry.coordinates[0] <= ne.lng &&
        point.geometry.coordinates[0] >= sw.lng &&
        point.geometry.coordinates[1] <= ne.lat &&
        point.geometry.coordinates[1] >= sw.lat
    );
  }, [data, currentBounds]);

  const updateBounds = useCallback((bounds: mapboxgl.LngLatBounds) => {
    setCurrentBounds({ ne: bounds.getNorthEast(), sw: bounds.getSouthWest() });
  }, []);

  return {
    dataInBounds,
    updateBounds
  };
};

export const usePointsInBounds = <T>(pointCollection?: FeatureCollection<Point, T>): PointsInBounds<T> => {
  const { dataInBounds, updateBounds } = useDataInBounds(pointCollection?.features);
  const pointsInBounds = useMemo<FeatureCollection<Point, T> | undefined>(() => {
    if (!pointCollection || !dataInBounds) return undefined;
    return { ...pointCollection, features: dataInBounds };
  }, [pointCollection, dataInBounds]);

  return {
    pointsInBounds,
    updateBounds
  };
};
